// Agenda de visitas: a nutricionista planeja os dias, é lembrada por e-mail de manhã e,
// às 19h, o sistema pergunta se as visitas foram feitas para registrar.
// Dono e admin veem e agendam para toda a equipe. Visualizadores e clientes não veem.
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useDocumentTitle, useMe, useTid } from '../lib/hooks';
import { addDaysIso, fmtIsoLong, MESES_LONG, plural, relDay, todayIso, weekdayIso } from '../lib/format';
import type { AgendaItem, AgendaList, AgendaResumo, TenantInfo } from '../lib/types';
import { Button, ErrorState, Loading } from '../components/ui';
import { AgendaCard, AgendaForm, ConfirmBlock, useAgendaActions } from '../components/AgendaParts';

const DIAS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const COR = { agendada: 'var(--accent)', realizada: '#9FBBAA', nao_realizada: 'var(--nc)' } as const;

export function Agenda() {
  const tid = useTid();
  const info = useOutletContext<TenantInfo>();
  const meId = useMe().data?.user.id ?? '';
  useDocumentTitle('Agenda');
  const [params, setParams] = useSearchParams();
  const hoje = todayIso();
  const [sel, setSel] = useState(() => params.get('dia') || hoje);
  const [ym, setYm] = useState(() => ({ y: +sel.slice(0, 4), m: +sel.slice(5, 7) - 1 }));
  const [quem, setQuem] = useState<string>('');
  const [form, setForm] = useState<{ item: AgendaItem | null; dia: string } | null>(null);
  const confirmar = params.get('confirmar') === '1';
  const actions = useAgendaActions(tid);

  // Intervalo do mês com as semanas completas (segunda a domingo).
  const first = `${ym.y}-${String(ym.m + 1).padStart(2, '0')}-01`;
  const offset = (weekdayIso(first) + 6) % 7;
  const de = addDaysIso(first, -offset);
  const ate = addDaysIso(de, 41);
  const q = useQuery({
    queryKey: ['agenda', tid, de, quem],
    queryFn: () => api.get<AgendaList>(`/api/t/${tid}/agenda?de=${de}&ate=${ate}${quem ? `&userId=${quem}` : ''}`),
    placeholderData: (prev) => prev,
  });
  const resumo = useQuery({ queryKey: ['agenda', tid, 'resumo'], queryFn: () => api.get<AgendaResumo>(`/api/t/${tid}/agenda/resumo`) });
  const d = q.data;

  useEffect(() => {
    const dia = params.get('dia');
    if (dia && dia !== sel) { setSel(dia); setYm({ y: +dia.slice(0, 4), m: +dia.slice(5, 7) - 1 }); }
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const byDay = useMemo(() => {
    const m = new Map<string, AgendaItem[]>();
    for (const it of d?.items ?? []) m.set(it.data, [...(m.get(it.data) ?? []), it]);
    return m;
  }, [d]);

  if (info.role === 'visualizador') return <Navigate to={`/t/${tid}`} replace />;

  const cells = Array.from({ length: 42 }, (_, i) => addDaysIso(de, i));
  const lastRowUsed = cells.slice(35).some((c) => c.slice(5, 7) === first.slice(5, 7));
  const shown = lastRowUsed ? cells : cells.slice(0, 35);
  const shift = (n: number) => setYm(({ y, m }) => ({ y: m + n < 0 ? y - 1 : m + n > 11 ? y + 1 : y, m: (m + n + 12) % 12 }));
  const pick = (dia: string) => {
    setSel(dia);
    if (dia.slice(0, 7) !== first.slice(0, 7)) setYm({ y: +dia.slice(0, 4), m: +dia.slice(5, 7) - 1 });
    const p = new URLSearchParams(params);
    p.set('dia', dia);
    p.delete('confirmar');
    setParams(p, { replace: true });
  };
  const doDia = byDay.get(sel) ?? [];
  const podeTodos = !!d?.podeTodos;
  const verPessoa = podeTodos && !quem;
  const semana = Array.from({ length: 7 }, (_, i) => addDaysIso(hoje, i));
  const proximos = semana.flatMap((dia) => (byDay.get(dia) ?? []).filter((i) => i.status === 'agendada'));
  const confirmItems = (resumo.data?.confirmar ?? []).filter((i) => !quem || i.userId === quem);

  return (
    <div>
      <div className="row between gap-10 wrap" style={{ marginBottom: 4 }}>
        <h1 className="h1">Agenda</h1>
        <Button variant="dark" onClick={() => setForm({ item: null, dia: sel < hoje ? hoje : sel })}>+ Agendar visita</Button>
      </div>
      <div className="sub" style={{ marginBottom: 14 }}>
        Você recebe um e-mail de manhã com as visitas do dia e, às 19h, o Mesa pergunta se elas foram feitas.
      </div>

      {podeTodos && d && d.pessoas.length > 1 && (
        <div className="row gap-6 wrap" style={{ marginBottom: 14 }} role="radiogroup" aria-label="Agenda de quem">
          <button className={`chip ${quem === '' ? 'on' : ''}`} onClick={() => setQuem('')}>Toda a equipe</button>
          {d.pessoas.map((p) => (
            <button key={p.userId} className={`chip ${quem === p.userId ? 'on' : ''}`} onClick={() => setQuem(p.userId)}>
              {p.userId === meId ? 'Minha agenda' : p.nome.split(' ').slice(0, 2).join(' ')}
            </button>
          ))}
        </div>
      )}

      {q.error && <ErrorState message="Não foi possível carregar a agenda." onRetry={() => q.refetch()} />}

      <div className="stack gap-14" style={{ marginBottom: 16 }}>
        <ConfirmBlock tid={tid} items={confirmItems} hoje={hoje} highlight={confirmar} onEdit={(it) => setForm({ item: it, dia: it.data })} actions={actions} />
      </div>

      <div className="ag-layout">
        <section className="card">
          <div className="row gap-8" style={{ marginBottom: 12 }}>
            <button className="icon-btn lg" onClick={() => shift(-1)} aria-label="Mês anterior">←</button>
            <div className="flex1" style={{ textAlign: 'center', fontSize: 15, fontWeight: 600 }}>{MESES_LONG[ym.m].replace(/^./, (c) => c.toUpperCase())} {ym.y}</div>
            <button className="icon-btn lg" onClick={() => shift(1)} aria-label="Próximo mês">→</button>
          </div>
          <div className="ag-cal">
            {DIAS.map((x) => <div key={x} className="wd">{x}</div>)}
            {shown.map((dia) => {
              const its = byDay.get(dia) ?? [];
              const out = dia.slice(0, 7) !== first.slice(0, 7);
              return (
                <button
                  key={dia}
                  className={`ag-day ${out ? 'out' : ''} ${dia === hoje ? 'today' : ''} ${dia === sel ? 'sel' : ''}`}
                  onClick={() => pick(dia)}
                  aria-label={`${fmtIsoLong(dia)}${its.length ? `: ${plural(its.length, 'visita', 'visitas')}` : ''}`}
                  aria-pressed={dia === sel}
                >
                  <span className="n">{+dia.slice(8, 10)}</span>
                  {its.length > 0 && (
                    <>
                      <span className="dots">{its.slice(0, 4).map((it) => <i key={it.id} style={{ background: COR[it.status] }} />)}</span>
                      <span className="names">
                        {its.slice(0, 3).map((it) => (
                          <span key={it.id} style={{ background: it.status === 'agendada' ? 'var(--accent-soft)' : it.status === 'realizada' ? 'var(--soft)' : 'var(--nc-soft)', color: it.status === 'nao_realizada' ? 'var(--nc-text)' : 'var(--accent-dark)', textDecoration: it.status === 'nao_realizada' ? 'line-through' : undefined }}>
                            {it.hora ? `${it.hora} ` : ''}{it.restaurantNome}
                          </span>
                        ))}
                        {its.length > 3 && <span style={{ color: 'var(--text-2)' }}>+{its.length - 3}</span>}
                      </span>
                    </>
                  )}
                </button>
              );
            })}
          </div>
          <div className="row gap-12 wrap small" style={{ marginTop: 12 }}>
            <span className="row gap-4"><i style={{ width: 8, height: 8, borderRadius: 4, background: COR.agendada, display: 'inline-block' }} /> agendada</span>
            <span className="row gap-4"><i style={{ width: 8, height: 8, borderRadius: 4, background: COR.realizada, display: 'inline-block' }} /> realizada</span>
            <span className="row gap-4"><i style={{ width: 8, height: 8, borderRadius: 4, background: COR.nao_realizada, display: 'inline-block' }} /> não realizada</span>
            {sel !== hoje && <button className="link-btn u" style={{ marginLeft: 'auto' }} onClick={() => pick(hoje)}>Ir para hoje</button>}
          </div>
        </section>

        <section>
          <div className="section-head" style={{ alignItems: 'center' }}>
            <h2 className="h2">{relDay(sel, hoje) === 'Hoje' ? `Hoje · ${fmtIsoLong(sel)}` : fmtIsoLong(sel).replace(/^./, (c) => c.toUpperCase())}</h2>
            <button className="link-btn" onClick={() => setForm({ item: null, dia: sel < hoje ? hoje : sel })}>+ Agendar{sel >= hoje ? ' neste dia' : ''}</button>
          </div>
          {!d ? (
            <Loading rows={2} height={90} />
          ) : doDia.length ? (
            <div className="stack gap-8">
              {doDia.map((it) => <AgendaCard key={it.id} tid={tid} it={it} hoje={hoje} showPerson={verPessoa} onEdit={(x) => setForm({ item: x, dia: x.data })} actions={actions} />)}
            </div>
          ) : (
            <div className="empty">
              Nenhuma visita {sel < hoje ? 'agendada neste dia' : 'agendada para este dia'}.
              {sel >= hoje && <div style={{ marginTop: 10 }}><Button size="sm" onClick={() => setForm({ item: null, dia: sel })}>Agendar visita para {relDay(sel, hoje).toLowerCase()}</Button></div>}
            </div>
          )}

          {sel === hoje && proximos.filter((i) => i.data !== hoje).length > 0 && (
            <>
              <h2 className="h2" style={{ margin: '20px 0 10px' }}>Próximos 7 dias</h2>
              <div className="stack gap-8">
                {proximos.filter((i) => i.data !== hoje).map((it) => (
                  <AgendaCard key={it.id} tid={tid} it={it} hoje={hoje} showDay showPerson={verPessoa} onEdit={(x) => setForm({ item: x, dia: x.data })} actions={actions} />
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      <AgendaForm
        tid={tid}
        open={!!form}
        onClose={() => setForm(null)}
        item={form?.item ?? null}
        dia={form?.dia ?? sel}
        pessoas={d?.pessoas ?? []}
        meId={meId}
        podeTodos={podeTodos}
        pessoaPadrao={quem || undefined}
      />
      {actions.confirmNode}
    </div>
  );
}
